import { LoadingButton } from '@mui/lab';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField } from '@mui/material';
import { ReactElement, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { MessagingTestResult } from '../../api/schema';
import toastOptions from '../../helpers/toastOptions';
import { messagingTestSending, sendMessagingTest } from '../../redux/slices/messaging/messagingSlice';
import { useDispatch, useSelector } from '../../redux/store';

type TestSendDialogProps = {
  channel: 'email' | 'sms';
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
};

export default function TestSendDialog({ channel, open, onClose, onSent }: TestSendDialogProps): ReactElement {
  const dispatch = useDispatch();
  const sending = useSelector(messagingTestSending);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [result, setResult] = useState<MessagingTestResult | undefined>();

  const handleSend = useCallback(async () => {
    setResult(undefined);
    try {
      const response = await dispatch(sendMessagingTest({ channel, to, message })).unwrap();
      const testResult = response.data?.result;
      setResult(testResult);
      if (testResult?.error) {
        toast.error(`Test ${channel} failed: ${testResult.error}`, toastOptions);
      } else if (testResult?.skipped) {
        toast.warning('Send is disabled in this environment (SEND_INVITE=false) — logged as skipped', toastOptions);
      } else {
        toast.success(`Test ${channel} sent to ${testResult?.to}`, toastOptions);
      }
      onSent?.();
    } catch (error) {
      console.log(error); // eslint-disable-line
    }
  }, [dispatch, channel, to, message, onSent]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Send test {channel === 'email' ? 'email' : 'SMS'}</DialogTitle>
      <DialogContent>
        <Stack spacing={4} mt={2}>
          <TextField
            label={channel === 'email' ? 'To (email address)' : 'To (phone number)'}
            placeholder="Leave empty to send to yourself"
            value={to}
            onChange={(event) => setTo(event.target.value)}
            fullWidth
            size="small"
          />
          <TextField
            label="Message"
            placeholder="TheTagSystem messaging test"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
          />
          {result && !result.error && !result.skipped && (
            <Alert severity="success">
              Sent via {result.provider}
              {result.message_id ? ` — message id ${result.message_id}` : ''}
            </Alert>
          )}
          {result?.skipped && <Alert severity="warning">Send is disabled in this environment; the attempt was logged as skipped.</Alert>}
          {result?.error && <Alert severity="error">{result.error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <LoadingButton onClick={handleSend} loading={!!sending} variant="contained">
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
